import type { DecodeMoveView, DecodeRunView } from '@aztec-realms/shared-types';

const PREFIX = 'aztec-realms:decode-draft:';
const MAX_DRAFTS = 12;
const MAX_MOVES = 512;

function key(owner: string, run: DecodeRunView) {
  return `${PREFIX}${owner}:${run.runId}`;
}

/** Transient input only, never a completion or balance. The server replays every move. */
export function readDecodeDraft(owner: string | undefined, run: DecodeRunView): DecodeMoveView[] {
  if (!owner || run.status === 'solved') return [];
  try {
    const draft = JSON.parse(sessionStorage.getItem(key(owner, run)) ?? 'null');
    if (
      !draft ||
      draft.baseMoves !== run.currentMoves ||
      !Array.isArray(draft.moves) ||
      draft.moves.length > MAX_MOVES
    )
      return [];
    let elapsed = 0;
    for (const move of draft.moves) {
      if (
        !move ||
        !Number.isInteger(move.clientElapsedMs) ||
        move.clientElapsedMs < elapsed ||
        !['clockwise', 'counter_clockwise'].includes(move.direction)
      )
        return [];
      elapsed = move.clientElapsedMs;
      if (move.moveType === 'rotate_segment') {
        if (
          !run.puzzle.segments.some((segment) => segment.id === move.segmentId && !segment.locked)
        )
          return [];
      } else if (move.moveType === 'rotate_ring') {
        if (!run.puzzle.rotatableRingIndices?.includes(move.ringIndex)) return [];
      } else return [];
    }
    return draft.moves;
  } catch {
    return [];
  }
}

export function saveDecodeDraft(
  owner: string | undefined,
  run: DecodeRunView,
  moves: readonly DecodeMoveView[],
): void {
  if (!owner) return;
  try {
    const draftKey = key(owner, run);
    if (run.status === 'solved' || moves.length === 0) {
      sessionStorage.removeItem(draftKey);
      return;
    }
    if (moves.length > MAX_MOVES) return;
    const keys = Object.keys(sessionStorage).filter(
      (item) => item.startsWith(PREFIX) && item !== draftKey,
    );
    while (keys.length >= MAX_DRAFTS) sessionStorage.removeItem(keys.shift()!);
    sessionStorage.setItem(draftKey, JSON.stringify({ baseMoves: run.currentMoves, moves }));
  } catch {
    /* Storage denial must never block gameplay. */
  }
}

export function draftRotations(
  run: DecodeRunView,
  moves: readonly DecodeMoveView[],
): Record<string, number> {
  const rotations = Object.fromEntries(
    run.puzzle.segments.map((segment) => [segment.id, segment.rotation]),
  );
  for (const move of moves) {
    for (const segment of run.puzzle.segments) {
      if (
        move.moveType === 'rotate_ring'
          ? segment.ringIndex === move.ringIndex
          : segment.id === move.segmentId
      ) {
        rotations[segment.id] =
          ((rotations[segment.id] ?? 0) + (move.direction === 'clockwise' ? 1 : -1) + 4) % 4;
      }
    }
  }
  return rotations;
}
